import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { getApiBase } from '../utils/apiBase';

const API_URL = getApiBase() + '/api';

const REWARD_LABELS = {
  mission_xp: (amount) => `+${amount} XP`,
  activation_bonus: () => 'Bonus aktivasyon kodu',
};

const DailyStreakCard = () => {
  const [state, setState] = useState({ loaded: false, data: null });
  const [claiming, setClaiming] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/me/streak`, { withCredentials: true });
      setState({ loaded: true, data: r.data });
    } catch (_) {
      setState({ loaded: true, data: null });
    }
  }, []);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  const claim = async () => {
    setClaiming(true);
    try {
      const r = await axios.post(`${API_URL}/me/streak/claim`, {}, { withCredentials: true });
      const reward = r.data?.reward || {};
      const cos = r.data?.granted_cosmetic;
      let label;
      if (cos) {
        label = `Yeni profil arka planı: ${cos.name}`;
      } else if (reward.kind === 'mission_xp_bonus') {
        label = `+${reward.amount} XP bonus (kozmetik koleksiyonun tam!)`;
      } else if (reward.kind === 'mission_xp') {
        label = `+${reward.amount} XP kazandın`;
      } else {
        label = 'Ödülünü aldın';
      }
      toast.success(label);
      await fetchStatus();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === 'already_claimed') {
        const hours = detail?.next_eligible_in_hours || 0;
        toast.info(`Yarın bekle (~${hours} saat sonra).`);
      } else {
        toast.error('Ödül alınamadı.');
      }
      await fetchStatus();
    } finally {
      setClaiming(false);
    }
  };

  if (!state.loaded) return null;
  if (!state.data) return null;

  const data = state.data;
  const currentDay = data.current_day || 1;
  const streak = data.streak_count || 0;

  return (
    <div className="glass-effect rounded-xl p-4 border border-white/10">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-white">Günlük seri</h3>
          <p className="text-[11px] text-zinc-400">
            {streak > 1 ? `${streak} gündür ardışık geldin` : 'Bugün başladın'}
          </p>
        </div>
        <div className="text-2xl">🔥</div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-3">
        {data.rewards_table?.map((r) => {
          const isToday = r.day === currentDay;
          const isPast = r.day < currentDay;
          return (
            <div
              key={r.day}
              className={
                'text-center text-[10px] rounded-md py-1.5 ' +
                (isToday
                  ? 'bg-violet-600 text-white font-semibold ring-2 ring-violet-400'
                  : isPast
                  ? 'bg-violet-900/40 text-violet-200'
                  : 'bg-white/5 text-zinc-500')
              }
            >
              <div>{r.day}</div>
              <div className="text-[9px] mt-0.5">
                {r.kind === 'mission_xp' ? `${r.amount}XP` : '🎨'}
              </div>
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        onClick={claim}
        disabled={!data.can_claim || claiming}
        className="w-full h-9 btn-primary text-xs font-semibold"
        data-testid="claim-daily-reward"
      >
        {claiming
          ? 'Alınıyor...'
          : !data.can_claim
          ? 'Yarın tekrar gel'
          : data.next_reward?.kind === 'cosmetic_or_xp'
          ? '7. gün ödülü: profil arka planı 🎨'
          : `Bugünün ödülünü al (+${data.next_reward?.amount} XP)`}
      </Button>
    </div>
  );
};

export default DailyStreakCard;
